import {Injectable} from '@angular/core';
import {HttpErrorResponse, HttpResponse} from '@angular/common/http';
import {BehaviorSubject, Observable} from 'rxjs';
import {distinctUntilChanged} from 'rxjs/operators';
import {environment} from '@environments/environment';
import {loadLatestBuild, refetchBuild} from '@app/_helpers/chunk-load-recovery';

const VERSION = /^[A-Za-z0-9][A-Za-z0-9._+-]{0,63}$/;
const NUMERIC = /^\d+(?:\.\d+)*$/;
const REQUIRED_LOAD_KEY = 'appVersionRequiredLoad';

/**
 * Notices when a newer release is running than the one this tab loaded.
 *
 * Every API response carries X-App-Version (snoball_server api/helpers/AppVersion.js). The
 * first value this tab sees is its own release - kept in memory, not storage, so a tab still
 * on the old bundle keeps noticing after another tab has reloaded. A later value that differs
 * marks an update available and it stays marked until the page reloads: during a deploy
 * old and new instances answer side by side, and the button must not flicker. Where both
 * values are dotted numbers only a NEWER one counts, so a tab that first met a new instance
 * is not told to update by an old one.
 *
 * X-App-Version-Required names the oldest release a tab may keep running. A tab whose own is
 * older is not only offered the update: its next page change loads the current build.
 */
@Injectable({providedIn: 'root'})
export class AppVersionService {
  static readonly HEADER = 'X-App-Version';
  static readonly REQUIRED_HEADER = 'X-App-Version-Required';

  /** Only the API names the release; asset and third-party responses are ignored. */
  apiBaseUrl: string = environment.apiUrl || '';

  /** Swappable for specs. */
  refetch: typeof refetchBuild = refetchBuild;

  private baseline: string | null = null;
  private latest: string | null = null;
  private required: string | null = null;
  private entryFilesRefetched: Promise<void> | null = null;
  private readonly updateSubject = new BehaviorSubject<boolean>(false);

  readonly updateAvailable$: Observable<boolean> = this.updateSubject.pipe(distinctUntilChanged());

  get currentVersion(): string | null {
    return this.baseline;
  }

  get latestVersion(): string | null {
    return this.latest;
  }

  /** The required release, once a response has named one newer than this tab's own. */
  get requiredVersion(): string | null {
    return this.required;
  }

  get updateAvailable(): boolean {
    return this.updateSubject.value;
  }

  observe(response: HttpResponse<unknown> | HttpErrorResponse, requestUrl: string): void {
    if (this.apiBaseUrl && !(requestUrl || '').startsWith(this.apiBaseUrl)) {
      return;
    }
    const version = response?.headers?.get(AppVersionService.HEADER)?.trim();
    if (!version || !VERSION.test(version)) {
      return;
    }
    if (this.baseline === null) {
      this.baseline = version;
    } else if (version !== this.baseline && this.isNewer(version, this.baseline)) {
      this.latest = version;
      this.updateSubject.next(true);
    }

    const required = response?.headers?.get(AppVersionService.REQUIRED_HEADER)?.trim();
    if (!this.required && required && VERSION.test(required) && required !== this.baseline &&
      this.isNewer(required, this.baseline)) {
      this.required = required;
      this.updateSubject.next(true);
      // Now rather than at the page change, so that load has little left to wait for.
      this.entryFilesRefetched = this.refetch();
    }
  }

  /** A stale bundle noticed another way - a lazy chunk the server no longer has. */
  markStale(): void {
    this.updateSubject.next(true);
  }

  reload(load: () => Promise<void> = () => loadLatestBuild()): void {
    load();
  }

  /**
   * Loads `url` as a full page of the current build, past the browser cache, when a required
   * release is newer than this tab's own. Called at a page change the route guards have
   * allowed, so whatever the page still had to save has been saved. Once per required release
   * per tab: a tab that comes back still older - an old instance answered it first during the
   * deploy - is offered the update instead of going round again. Returns whether it loads.
   */
  loadRequired(url: string, assign: (target: string) => void = (target) => window.location.assign(target)): boolean {
    if (!this.required || (typeof navigator !== 'undefined' && navigator.onLine === false)) {
      return false;
    }
    try {
      if (sessionStorage.getItem(REQUIRED_LOAD_KEY) === this.required) {
        return false;
      }
      sessionStorage.setItem(REQUIRED_LOAD_KEY, this.required);
    } catch (e) {
      return false;
    }
    Promise.all([this.entryFilesRefetched, this.refetch([url], {entryFiles: false})])
      .finally(() => assign(url));
    return true;
  }

  private isNewer(candidate: string, baseline: string): boolean {
    if (!NUMERIC.test(candidate) || !NUMERIC.test(baseline)) {
      return true;
    }
    const a = candidate.split('.').map(Number);
    const b = baseline.split('.').map(Number);
    for (let index = 0; index < Math.max(a.length, b.length); index++) {
      const difference = (a[index] || 0) - (b[index] || 0);
      if (difference !== 0) {
        return difference > 0;
      }
    }
    return false;
  }
}
